import { ref } from 'vue'

/**
 * Native HTML5 drag & drop voor het herordenen binnen één groep siblings.
 *
 * Herordenen over groepsgrenzen heen gebeurt bewust niet — verplaatsen naar
 * een andere categorie blijft via de menu-actie "Verplaats naar". Slepen is
 * additief bovenop de toetsenbord-route (omhoog/omlaag), nooit in plaats
 * ervan. De invoeg-positie volgt de cursorhelft (verticaal of horizontaal).
 *
 * Een "groep" is een willekeurige sleutel die één siblinglijst identificeert
 * (bijv. `sub-3` of `tabs-7`); alleen items met dezelfde groepssleutel
 * accepteren elkaars drop.
 */
export function useDragReorder(axis: 'y' | 'x' = 'y') {
  const dragGroup = ref<string | null>(null)
  const dragIndex = ref<number | null>(null)
  const dropIndex = ref<number | null>(null)

  function start(event: DragEvent, group: string, index: number) {
    dragGroup.value = group
    dragIndex.value = index
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move'
      // Firefox start zonder setData geen drag.
      event.dataTransfer.setData('text/plain', String(index))
    }
  }

  function over(event: DragEvent, group: string, index: number) {
    if (dragGroup.value !== group || dragIndex.value === null) return
    event.preventDefault() // nodig om droppen toe te staan
    if (event.dataTransfer) event.dataTransfer.dropEffect = 'move'
    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect()
    const before = axis === 'y'
      ? event.clientY < rect.top + rect.height / 2
      : event.clientX < rect.left + rect.width / 2
    dropIndex.value = before ? index : index + 1
  }

  /** Reordert `items` volgens de sleep-actie; null als er niets wijzigt. */
  function apply<T>(items: T[]): T[] | null {
    const from = dragIndex.value
    let to = dropIndex.value
    if (from === null || to === null) return null
    if (to > from) to-- // verwijderen vóór invoegen verschuift de doelindex
    if (to === from) return null
    const copy = [...items]
    const [moved] = copy.splice(from, 1)
    copy.splice(to, 0, moved)
    return copy
  }

  function reset() {
    dragGroup.value = null
    dragIndex.value = null
    dropIndex.value = null
  }

  const isActive = (group: string) => dragGroup.value === group && dragIndex.value !== null
  const isDragged = (group: string, index: number) => isActive(group) && dragIndex.value === index
  /** Invoeglijn vóór item `index`. */
  const lineBefore = (group: string, index: number) => isActive(group) && dropIndex.value === index
  /** Invoeglijn ná het laatste item (drop onderaan/achteraan). */
  const lineAfterLast = (group: string, index: number, count: number) =>
    isActive(group) && index === count - 1 && dropIndex.value === count

  return {
    dragGroup,
    dragIndex,
    dropIndex,
    start,
    over,
    apply,
    reset,
    isActive,
    isDragged,
    lineBefore,
    lineAfterLast,
  }
}
